import { ReactElement } from "react";
import { Link } from "react-router-dom";
import { localize } from "src/l10n";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { describeDue } from "../../rowText";
import { AttentionSurface, trackAttentionRowClick } from "../../tracking";
import { AttentionSourceType, IAttentionRow } from "../../types";
import "./AttentionRow.scss";

interface IProps {
    row: IAttentionRow;

    /**
     * The most urgent row gets the filled action. More than one filled action in a
     * short list makes none of them read as the first thing to do.
     */
    isPrimary?: boolean;

    /**
     * Which of the four places this row is being shown in, and where it sits in
     * that list. Only used for the tracking.
     */
    surface: AttentionSurface;
    position: number;
}

/**
 * Icon names are case sensitive and must match the svg files in
 * public/visage2icons exactly. The wrong case works on a Windows dev machine and
 * fails everywhere else.
 */
const iconBySource: Record<AttentionSourceType, string> = {
    [AttentionSourceType.GroupInvite]: "profile-add",
    [AttentionSourceType.NewsAcknowledgement]: "tick-circle",
    [AttentionSourceType.LearningCourse]: "book",
    [AttentionSourceType.FormResponse]: "clipboard-text",
};

const chipBySource: Record<AttentionSourceType, string> = {
    [AttentionSourceType.GroupInvite]: "ATTENTION_TYPE_INVITE",
    [AttentionSourceType.NewsAcknowledgement]: "ATTENTION_TYPE_ACKNOWLEDGE",
    [AttentionSourceType.LearningCourse]: "ATTENTION_TYPE_COURSE",
    [AttentionSourceType.FormResponse]: "ATTENTION_TYPE_FORM",
};

const actionBySource: Record<AttentionSourceType, string> = {
    [AttentionSourceType.GroupInvite]: "ATTENTION_ACTION_ANSWER",
    [AttentionSourceType.NewsAcknowledgement]: "ATTENTION_ACTION_ACKNOWLEDGE",
    [AttentionSourceType.LearningCourse]: "ATTENTION_ACTION_CONTINUE",
    [AttentionSourceType.FormResponse]: "ATTENTION_ACTION_OPEN_ANSWER",
};

/**
 * "Du · obligatorisk · förfaller ons 30 sep". The deadline half comes from
 * describeDue, which the menu band uses too, so the two never word it
 * differently.
 */
function buildMeta(row: IAttentionRow): string {
    const parts: string[] = [localize("ATTENTION_META_YOU")];

    if (row.isMandatory && row.sourceType === AttentionSourceType.LearningCourse) {
        parts.push(localize("ATTENTION_META_MANDATORY"));
    }

    const due = describeDue(row);

    if (due) {
        parts.push(due);
    }

    return parts.join(" · ");
}

function AttentionRow(props: IProps): ReactElement {
    const { row, isPrimary, surface, position } = props;

    const classNames = [
        "AttentionRow",
        `AttentionRow-source-${row.sourceType}`,
    ].join(" ");

    return (
        <Link
            className={classNames}
            to={row.url}
            onClick={() => trackAttentionRowClick(row, surface, position)}
            aria-label={`${localize(chipBySource[row.sourceType])} · ${row.title} · ${buildMeta(row)}`}
        >
            <span className="AttentionRow-icon" aria-hidden="true">
                <Visage2Icon icon={iconBySource[row.sourceType]} size="small" />
            </span>

            <span className="AttentionRow-text">
                <span className="AttentionRow-chips">
                    <span className="AttentionRow-chip">
                        {localize(chipBySource[row.sourceType])}
                    </span>
                    {row.isOverdue && (
                        <span className="AttentionRow-chip AttentionRow-chip--late">
                            <span className="AttentionRow-dot" aria-hidden="true" />
                            {localize("ATTENTION_OVERDUE")}
                        </span>
                    )}
                </span>

                <span className="AttentionRow-title">{row.title}</span>
                <span className="AttentionRow-meta">{buildMeta(row)}</span>
            </span>

            {/* Looks like a button but is not one. The whole row is the link, so
                there is a single tab stop and no nested interactive element. */}
            <span
                className={isPrimary ? "AttentionRow-action AttentionRow-action--primary" : "AttentionRow-action"}
                aria-hidden="true"
            >
                {localize(actionBySource[row.sourceType])}
            </span>
        </Link>
    );
}

export default AttentionRow;
